import { CustomClaimValue, DeviceMediaRequest, getApiUrl, MediaRequestStatuses, useAuth } from "@app/shared";
import { ReactNode } from "react";
import { Link } from ".";
import { useNavigate } from "react-router-dom";
import { Pages } from "../pages";
import { DeviceTab } from "../pages/facility/deviceTabs";
import { MediaNoticeList } from "./MediaNoticeList";

// The "Result" column answers two questions WITHOUT hovering — "why is the outcome what it is?" (the
// quality notices up top) and "what can I download?" (a uniform row of artifacts). 
export const MediaRequestDownloadLinks: React.FC<{ request: DeviceMediaRequest }> = ({ request }) => {
    const user = useAuth();
    const isSuperAdmin = !!user.userClaims?.find(uc => uc.claims.find(c => c === CustomClaimValue.SuperAdmin));
    const navigate = useNavigate();

    const gotoDeviceErrors = (deviceId: string) => {
        navigate(Pages.DeviceDetails.getUrlWithQuery({ id: deviceId }, { tab: DeviceTab.Errors }));
    }

    const handleVideoDownload = () => {
        window.open(getApiUrl(`/api/mediarequest/${request.deviceId}/media-requests/${request.id}/video/download`), '_blank');
    }
    const handleVideoView = () => {
        window.location.assign(getApiUrl(`/api/mediarequest/${request.deviceId}/media-requests/${request.id}/video/view`));
    };
    const handleDataDownload = () => {
        window.location.assign(getApiUrl(`/api/mediarequest/${request.deviceId}/media-requests/${request.id}/targets`));
    }
    const handleRadarDownload = () => {
        window.location.assign(getApiUrl(`/api/mediarequest/${request.deviceId}/media-requests/${request.id}/radar`));
    }
    const handleSupportFilesDownload = () => {
        window.location.assign(getApiUrl(`/api/mediarequest/${request.deviceId}/media-requests/${request.id}/support-files`));
    }

  
    const hasVideo = !!request.videoUrl;
    const hasRadar = !!request.radarUrl;
    // Support zip bundles internal sensor/tracker config — only super admins see it.
    const hasSupportFiles = !!request.supportFilesUrl && isSuperAdmin;
    const showVideoSection = hasVideo || request.includeVideo === true;
    const showRadarSection = hasRadar || request.includeRadarData === true;
    const isError = request?.status === MediaRequestStatuses.Error;
    // "Unavailable" is only true once the outcome is settled — while a request is still recording /
    // merging / uploading, a missing URL just means "not produced yet", so we don't label it.
    const isTerminal = request.status === MediaRequestStatuses.Complete
        || request.status === MediaRequestStatuses.PartiallyComplete
        || request.status === MediaRequestStatuses.Error
        || request.status === MediaRequestStatuses.Expired
        || request.status === MediaRequestStatuses.Cancelled;

    // One uniform shape for every artifact: a same-styled name, then its actions (or "unavailable").
    const artifact = (name: string, body: ReactNode) => (
        <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
            <span className="font-medium text-body-text">{name}</span>
            {body}
        </span>
    );
    const unavailable = <span className="text-gray-400">unavailable</span>;
    const dot = <span className="text-gray-300" aria-hidden>·</span>;

    return (
        <div className="flex flex-col gap-1.5 text-sm">
            {/* Why — always visible, no hover required (works on touch / small screens). */}
            <MediaNoticeList notices={request.notices} isSuperAdmin={isSuperAdmin} />

            {/* What's available — uniform tokens, consistent styling row to row. */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                {showVideoSection && artifact("Video", hasVideo ? (
                    <span className="inline-flex items-center gap-1.5">
                        <Link onClick={handleVideoView}>View</Link>
                        {dot}
                        <Link onClick={handleVideoDownload}>Download (mp4)</Link>
                    </span>
                ) : isTerminal ? unavailable : <span className="text-gray-400">processing…</span>)}

                {showRadarSection && artifact("Radar", hasRadar ? (
                    <Link onClick={handleRadarDownload}>Download (bin)</Link>
                ) : isTerminal ? unavailable : <span className="text-gray-400">processing…</span>)}

                {hasSupportFiles && artifact("Config", <Link onClick={handleSupportFilesDownload}>Download (zip)</Link>)}
                {artifact("Targets", <Link onClick={handleDataDownload}>Download (csv)</Link>)}
            </div>

            {/* Super admins get a quiet path to the underlying error log; everyone else doesn't need it. */}
            {isError && isSuperAdmin && (
                <Link onClick={() => gotoDeviceErrors(request.deviceId)} className="text-xs">View device errors →</Link>
            )}
        </div>
    );
}
